import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useIsDemoExperience } from "@av/apps/common-apps/platform/services/demo-experience/demo";
import { buildJobPostDescriptionSearchIndex } from "@av/apps/recruitment/ats-common/job-post-description-search";
import { getLocalizedJobPostContent } from "@av/apps/recruitment/ats-common/localized-job-post-content";
import { useMockDbStore } from "@av/apps/recruitment/ats-common/mock-db/mock-db-store";
import { publicJobPostQueryKeys } from "@av/apps/recruitment/ats-common/services/job-posts/query-keys";
import type {
  JobPostFilters,
  MockJobPostData,
  PublicJobPostDetailsModel,
  PublicJobPostModel,
} from "@av/apps/recruitment/ats-common/types";
import { bffAxios } from "@av/config/api-config/axios-config";
import { type Dayjs, dayjs } from "@av/config/extended-dayjs";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";

const PAGE_SIZE = 6;

/**
 * Normalize a raw `applyBy` close date into a Dayjs, dropping anything that does
 * not parse to a valid date. Returning `undefined` for an invalid value lets the
 * `applyBy &&` display guards hide the candidate "Accepting applications until"
 * line entirely instead of rendering "Invalid Date".
 */
const toValidApplyBy = (value?: Dayjs | string | null): Dayjs | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed : undefined;
};

const toPublishedApplyBy = (
  value?: Dayjs | string | null,
  includeApplyByOnJobPost?: boolean
): Dayjs | undefined => {
  return includeApplyByOnJobPost ? toValidApplyBy(value) : undefined;
};

export const usePublicFetchJobPosts = (
  companyId: string | undefined,
  jobPostFilters: JobPostFilters
) => {
  const isDemoExperience = useIsDemoExperience();
  const mockedJobPosts = useMockDbStore((state) => state.jobPosts);
  const { searchText = "" } = jobPostFilters;
  const normalizedSearchText = searchText.toLowerCase().trimEnd();
  const hasSearchText = normalizedSearchText !== "";
  const { locale } = useLocaleStore();

  const query = useQuery({
    /**
     * Change query key when filtering happens in the backend
     */
    queryKey: [...publicJobPostQueryKeys.list(jobPostFilters), companyId] as const,
    enabled: isDemoExperience ? true : !!companyId,
    queryFn: async () => {
      if (!isDemoExperience) {
        if (!companyId) {
          return [];
        }

        const { data: jobPosts } = await bffAxios.get<{ items: PublicJobPostModel[] }>(
          `/public/companies/${companyId}/job-posts`
        );

        return jobPosts.items as Array<Partial<PublicJobPostDetailsModel>>;
      }

      return mockedJobPosts;
    },
  });

  // Gated on a boolean so the index is built once when a search starts, not on every keystroke.
  const descriptionSearchIndex = useMemo(
    () =>
      hasSearchText
        ? buildJobPostDescriptionSearchIndex(query.data ?? [], locale)
        : new Map<string, string>(),
    [query.data, locale, hasSearchText]
  );

  const mappedJobPosts = (query.data ?? []).map((job): PublicJobPostDetailsModel => {
    const { includeApplyByOnJobPost } = job as Partial<MockJobPostData>;

    return {
      ...(job as PublicJobPostDetailsModel),
      postedDate: job.postedDate ? dayjs(job.postedDate) : undefined,
      applyBy: isDemoExperience
        ? toPublishedApplyBy(job.applyBy, includeApplyByOnJobPost)
        : toValidApplyBy(job.applyBy),
    };
  });

  // Filtering can be moved to the backend
  const filteredJobPosts = mappedJobPosts.filter(
    (job) =>
      (!jobPostFilters.startDate || job.postedDate?.isAfter(jobPostFilters.startDate)) &&
      (!jobPostFilters.endDate || job.postedDate?.isBefore(jobPostFilters.endDate)) &&
      (!jobPostFilters.statuses ||
        !job.postStatus ||
        jobPostFilters.statuses?.length === 0 ||
        jobPostFilters.statuses?.includes(job.postStatus)) &&
      (!jobPostFilters.jobPostId || jobPostFilters.jobPostId === job.jobPostId) &&
      (!jobPostFilters.city ||
        !job.city ||
        jobPostFilters.city.length === 0 ||
        jobPostFilters.city?.some(
          (city) => city.trim().toLowerCase() === job.city?.trim().toLowerCase()
        )) &&
      (!jobPostFilters.locationType ||
        !job.locationType ||
        job.locationType === jobPostFilters.locationType) &&
      (!jobPostFilters.subdivisionCodes?.length ||
        (job.subdivisionCode && jobPostFilters.subdivisionCodes.includes(job.subdivisionCode))) &&
      (normalizedSearchText === "" ||
        getLocalizedJobPostContent(job.jobTitles, locale)
          .toLocaleLowerCase()
          .includes(normalizedSearchText) ||
        descriptionSearchIndex.get(job.jobPostId)?.includes(normalizedSearchText) ||
        String(job.postStatus)?.toLocaleLowerCase().includes(normalizedSearchText) ||
        job.city?.toLocaleLowerCase().includes(normalizedSearchText) ||
        (job.jobId && String(job.jobId).includes(normalizedSearchText)))
  );

  filteredJobPosts.sort((jobA, jobB) => {
    const a = jobA.postedDate?.valueOf() ?? 0;
    const b = jobB.postedDate?.valueOf() ?? 0;

    return b - a;
  });

  const visibleJobs = filteredJobPosts.filter((j) => j.postStatus === "POSTED");

  const pageCount = Math.max(1, Math.ceil(visibleJobs.length / PAGE_SIZE));
  const currentPage = Math.min(jobPostFilters.page ?? 1, pageCount);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedJobs = visibleJobs.slice(startIndex, startIndex + PAGE_SIZE);

  return {
    ...query,
    data: filteredJobPosts,
    pageSize: PAGE_SIZE,
    paginatedJobs,
    currentPage,
    visibleTotal: visibleJobs.length,
    findPageNumberForJob: (jobPostId: string) => {
      const jobLocationInList = visibleJobs.findIndex((j) => j.jobPostId === jobPostId);

      if (jobLocationInList < 0) {
        return currentPage;
      }

      return Math.floor(jobLocationInList / PAGE_SIZE) + 1;
    },
    findFirstJobInPage: (page: number) => {
      if (visibleJobs.length === 0) {
        return undefined;
      }

      const clampedPage = Math.min(Math.max(page, 1), pageCount);
      const jobIndex = (clampedPage - 1) * PAGE_SIZE;

      return visibleJobs[jobIndex] ?? visibleJobs[0];
    },
  };
};

export const usePublicFetchSingleJobPost = (companyId?: string, jobPostId?: string) => {
  const isDemoExperience = useIsDemoExperience();
  const mockedJobPosts = useMockDbStore((state) => state.jobPosts);

  return useQuery({
    queryKey: publicJobPostQueryKeys.detail(jobPostId),
    enabled: isDemoExperience ? true : !!companyId && !!jobPostId,
    queryFn: async () => {
      if (isDemoExperience) {
        const mockJobPost = mockedJobPosts.find((jobPost) => jobPost.jobPostId === jobPostId);

        if (!mockJobPost) {
          return {} as PublicJobPostDetailsModel;
        }

        const mockMappedJobPost: PublicJobPostDetailsModel = {
          jobPostId: mockJobPost.jobPostId ?? "",
          companyId: "",
          jobId: mockJobPost.jobId ?? 0,
          jobTitles: mockJobPost.jobTitles as Record<string, string>,
          descriptions: mockJobPost.descriptions as Record<string, string>,
          city: mockJobPost.city,
          streetAddress: mockJobPost.streetAddress,
          countryCode: mockJobPost.jobCountryCode,
          subdivisionCode: mockJobPost.subdivisionCode,
          employmentType: mockJobPost.employmentType,
          locationType: mockJobPost.locationType,
          postStatus: mockJobPost.postStatus,
          payType: mockJobPost.payType,
          minCompensation: mockJobPost.minCompensation,
          maxCompensation: mockJobPost.maxCompensation,
          payDisplay: mockJobPost.payDisplay ?? "RANGE",
          includeCompensationOnJobPost: mockJobPost.includeCompensationOnJobPost ?? false,
          currencyCode: mockJobPost.currencyCode,
          isResumeRequired: mockJobPost.isResumeRequired,
          isSupportingDocumentsRequired: mockJobPost.isSupportingDocumentsRequired,
          postedDate: mockJobPost.postedDate ? dayjs(mockJobPost.postedDate) : undefined,
          applyBy: toPublishedApplyBy(mockJobPost.applyBy, mockJobPost.includeApplyByOnJobPost),
          screeningQuestions: [],
        };

        return mockMappedJobPost;
      }

      const {
        data: { data },
      } = await bffAxios.get<{ data: PublicJobPostDetailsModel }>(
        `/public/companies/${companyId}/job-posts/${jobPostId}`
      );

      // The API only returns applyBy when the close date is published to
      // candidates; normalize it to a valid Dayjs so the display layer can render it.
      // Preserve a missing payload as-is so the not-found path still triggers.
      return data ? { ...data, applyBy: toValidApplyBy(data.applyBy) } : data;
    },
  });
};
